import {
  Tooltip,
  TooltipAncestorContext,
  TooltipProps,
} from '@toggle/design-system/src/components/tooltip/Tooltip';
import React, { useContext, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useTouchDevice } from '~/hooks/MediaQueryHook';

import styles from './TooltipContainer.module.scss';

export const useTruncationRef = <T extends HTMLElement = HTMLElement>() => {
  const observerRef = useRef<ResizeObserver | null>(null);
  const [isTruncated, setIsTruncated] = useState(false);

  const truncationRef = (el: T | null) => {
    observerRef.current?.disconnect();

    if (el) {
      const check = () =>
        setIsTruncated(
          el.scrollHeight > el.clientHeight || el.scrollWidth > el.clientWidth
        );
      check();
      observerRef.current = new ResizeObserver(check);
      observerRef.current.observe(el);
    }
  };

  return { truncationRef, isTruncated };
};

export type TooltipContainerProps = Omit<
  TooltipProps,
  'closeLabel' | 'isTouchDevice' | 'children'
> & {
  children?: TooltipProps['children'];
  showUnderline?: boolean;
  ref?: React.Ref<HTMLElement>;
};

export const TooltipContainer = (props: TooltipContainerProps) => {
  const isTouchDevice = useTouchDevice();
  const { t } = useTranslation('common');
  const isNestedInTooltip = useContext(TooltipAncestorContext);

  if (!props.children) {
    return null;
  }

  if (!props.label || isNestedInTooltip) {
    return props.children;
  }

  return (
    <Tooltip
      {...props}
      closeLabel={t('common:tapToClose')}
      isTouchDevice={isTouchDevice}
    >
      {props.showUnderline ? (
        <div className={styles.underlineText}>{props.children}</div>
      ) : (
        props.children
      )}
    </Tooltip>
  );
};
